import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Send, RefreshCw } from "lucide-react";
import api from "../lib/api";

export default function EmailStatusCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  useEffect(() => { api.get("/admin/email-status").then(({ data }) => setSt(data)).catch(() => setSt({ mode: "unknown" })); }, []);
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/email-test"); toast.success(`Email di test inviata a ${data.to} con PDF allegato`); }
    catch (e) { toast.error(e.response?.data?.detail || "Invio fallito"); }
    finally { setBusy(false); }
  };
  const recheck = async () => {
    setChecking(true);
    try { const { data } = await api.post("/admin/email-status/recheck"); setSt((prev) => ({ ...prev, key: data })); }
    catch (e) { toast.error(e.response?.data?.detail || "Controllo non riuscito"); }
    finally { setChecking(false); }
  };
  if (!st) return null;
  const byo = st.mode === "resend_byo";
  const key = st.key;
  const keyInvalid = key?.status === "invalid";
  return (
    <div className={`ga-card p-5 border ${byo ? "border-emerald-300" : "border-amber-300 bg-amber-50/40"}`} data-testid="email-status-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email transazionali</div>
        <button onClick={test} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="email-test-button"><Send className="w-3 h-3" /> {busy ? "Invio..." : "Invia email di test"}</button>
      </div>
      {byo ? (
        <div className="mt-2 text-sm text-emerald-900 flex items-start gap-2" data-testid="email-status-byo">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Mittente <b className="font-mono">{st.from_name} &lt;{st.from_email}&gt;</b>{st.reply_to && <> · risposte a <span className="font-mono">{st.reply_to}</span></>}. Il PDF del riepilogo mensile viene <b>allegato</b>.</div>
        </div>
      ) : (
        <div className="mt-2 text-sm text-amber-900 flex items-start gap-2" data-testid="email-status-disabled">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Le email non partono: mancano <span className="font-mono">RESEND_API_KEY</span> o <span className="font-mono">RESEND_FROM_EMAIL</span> nelle impostazioni del server su Render.
          </div>
        </div>
      )}
      {key && key.status !== "not_configured" && (
        <div className={`mt-3 rounded-xl border p-3 text-sm ${keyInvalid ? "border-red-300 bg-red-50 text-red-900" : "border-slate-200 bg-slate-50/60 text-slate-700"}`} data-testid="email-key-status" data-status={key.status}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-start gap-2">
              {keyInvalid ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
              <div>
                {keyInvalid && <b data-testid="email-key-invalid">La chiave Resend non è valida: le email non partono. Creane una nuova su resend.com e sostituiscila su Render, poi premi Manual Deploy.</b>}
                {key.status === "valid" && <>Chiave Resend valida{key.domain_verified === true && <> · dominio <span className="font-mono">{key.domain}</span> verificato</>}{key.domain_verified === false && <> · <b>dominio <span className="font-mono">{key.domain}</span> non verificato su Resend</b></>}.</>}
                {key.status === "unknown" && <>Non è stato possibile verificare la chiave Resend: il servizio non ha risposto.</>}
                <div className="text-xs opacity-80 mt-0.5">{key.checked_at ? <>Controllata il <span className="font-mono">{new Date(key.checked_at).toLocaleString("it-IT")}</span></> : "Controllo in corso all'avvio del server"}</div>
                {keyInvalid && key.error && <div className="text-xs font-mono text-red-700/80 mt-0.5 break-words">{key.error}</div>}
              </div>
            </div>
            <button onClick={recheck} disabled={checking} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50" data-testid="email-key-recheck-button"><RefreshCw className={`w-3 h-3 ${checking ? "animate-spin" : ""}`} /> {checking ? "Controllo..." : "Ricontrolla"}</button>
          </div>
        </div>
      )}
      {st.last_24h && (
        <div className="mt-3 text-xs text-slate-600" data-testid="email-status-last24h">
          Ultime 24 ore: <b>{st.last_24h.sent || 0}</b> inviate · <b>{st.last_24h.failed || 0}</b> fallite · <b>{st.last_24h.skipped || 0}</b> non inviate
        </div>
      )}
      {st.recent_failures?.length > 0 && (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50/60 p-3" data-testid="email-status-failures">
          <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1">Ultimi invii falliti</div>
          <ul className="space-y-1 text-xs text-red-900">
            {st.recent_failures.map((f, i) => (
              <li key={i} className="break-words">
                <span className="font-mono">{new Date(f.at).toLocaleString("it-IT")}</span> · {f.to} · {f.subject}
                {f.error && <div className="text-red-700/80 font-mono">{f.error}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
